import { isNegotiatedRateplan } from "../../slices/rateplanSlice";

// What the banner claimed for every property before the occupancy became
// per-room-type. Kept as the fallback so a room type Ops has not configured
// yet reads exactly as it did before.
export const DEFAULT_SHARED_OCCUPANCY = 2;

/**
 * How many adults the room type's standard rate actually covers.
 *
 * Only static (negotiated) rate plans are loaded by the supplier, so only they
 * carry a meaningful Standard Unit Occupancy. Dynamic CRS plans keep the old
 * hardcoded 2 — their rates are not built on a per-room-type sharing basis, so
 * a room type's SUO would say nothing true about them.
 *
 * @param {Object} rateplan the selected rate plan, `{ data: { additionalData } }`
 * @param {Object} roomtype the room type behind the active tab
 * @returns {number} adults the rate is based on; never null, so the caller
 *   always has something to render
 */
export const resolveSharedOccupancy = (rateplan, roomtype) => {
  if (!isNegotiatedRateplan(rateplan)) return DEFAULT_SHARED_OCCUPANCY;

  const standardUnitOccupancy = Number(
    roomtype?.additionalData?.standardUnitOccupancy
  );

  // Unset reaches us as null or undefined, and the column also accepts a
  // literal 0 that would render as "based on 0 adults sharing". Number(null)
  // is 0 and Number(undefined) is NaN, so one positivity check covers all
  // three.
  return Number.isFinite(standardUnitOccupancy) && standardUnitOccupancy > 0
    ? standardUnitOccupancy
    : DEFAULT_SHARED_OCCUPANCY;
};

/**
 * The banner sentence. Pluralised because a Standard Unit Occupancy of 1 is a
 * legitimate single-occupancy room type, and "1 adults" reads as a bug.
 */
export const buildOccupancyMessage = (occupancy) =>
  `Daily rates for single occupancy rooms apply to 1 adult. All other rates are based on ${occupancy} adult${
    occupancy === 1 ? "" : "s"
  } sharing.`;
